if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    get addonVariantId() {
      const raw = this.dataset.addonVariantId;
      if (!raw) return null;
      const id = Number(raw);
      return Number.isFinite(id) && id > 0 ? id : null;
    }

    shouldIncludeAddon(isSubscription) {
      const addonId = this.addonVariantId;
      if (!addonId) return false;
      const when = this.dataset.addonWhen || 'both';
      if (when === 'subscription') return isSubscription;
      if (when === 'one_time') return !isSubscription;
      return true;
    }

    buildSubscriptionGiftItem(quantity) {
      const addonId = this.addonVariantId;
      const productId = this.dataset.subscriptionProductId;
      if (!addonId || !productId) {
        if (addonId && !productId) {
          console.error('[product-form] Subscription gift is missing its product id');
        }
        return null;
      }

      const parsedQuantity = Number(quantity);
      const giftQuantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;

      const properties = {
        _bfb_subscription_gift: '1',
        _bfb_subscription_product_id: String(productId),
      };
      if (Object.prototype.hasOwnProperty.call(this.dataset, 'addonLabel')) {
        const label = this.dataset.addonLabel.trim();
        properties._bfb_subscription_gift_label = label || '__none__';
      }

      return {
        id: addonId,
        quantity: giftQuantity,
        properties,
      };
    }

    buildLineItemFromFormData(formData) {
      const variantId = Number(formData.get('id'));
      if (!Number.isFinite(variantId) || variantId <= 0) {
        throw new Error('Missing variant');
      }

      const item = {
        id: variantId,
        quantity: Number(formData.get('quantity') || 1),
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        item.selling_plan = Number(sellingPlan);
      }

      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = value;
        }
      }
      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      return item;
    }

    buildCartAddPayload(formData) {
      const parent = this.buildLineItemFromFormData(formData);
      const items = [parent];
      const isSubscription = Boolean(formData.get('selling_plan'));
      if (this.shouldIncludeAddon(isSubscription)) {
        const gift = this.buildSubscriptionGiftItem(parent.quantity);
        if (gift) items.push(gift);
      }

      const payload = { items };

      if (this.cart) {
        payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        payload.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      return payload;
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const formData = new FormData(this.form);
      const isSubscription = Boolean(formData.get('selling_plan'));
      let config;

      if (this.shouldIncludeAddon(isSubscription)) {
        config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        try {
          config.body = JSON.stringify(this.buildCartAddPayload(formData));
        } catch (buildError) {
          console.error(buildError);
          this.handleErrorMessage('Unable to add to cart. Please try again.');
          this.finishSubmit();
          return;
        }
      } else {
        config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form'});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.finishSubmit();
        });
    }

    finishSubmit() {
      this.submitButton.classList.remove('loading');
      if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    toggleSubmitButton(disable = true, text) {
      const addButton = this.querySelector('[name="add"]');
      const addButtonText = addButton?.querySelector('span');
      if (!addButton || !addButtonText) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    get variantIdInput() {
      return this.querySelector('[name="id"]');
    }
  });
}
